/**
 * Commission plan display helpers (TMMPDM-0041, MM-D-106). Formatting only: a rate in words and a
 * one-line summary of a plan's phases. Nothing here decides whether a plan is valid or compliant —
 * `status`, `noncompliant` and the preview's `valid` are the server's.
 */
import type {
  CommissionAfterEnd,
  CommissionAppliesTo,
  CommissionPhase,
  CommissionPlanMode,
  CommissionPlanView,
} from '@/api/tmm/types';
import { formatMoney, formatPercentBp } from '@/format';

/** Each enum's English fallback, by its wire value (`commissionPlans.<field>.<value>`). */
export const MODE_FALLBACK: Record<CommissionPlanMode, string> = {
  SIMPLE: 'Simple',
  ADVANCED: 'Advanced',
};
export const APPLIES_TO_FALLBACK: Record<CommissionAppliesTo, string> = {
  BASE: 'Base price',
  ALL: 'All lines',
};
export const AFTER_END_FALLBACK: Record<CommissionAfterEnd, string> = {
  STOP: 'Stop',
  HONOR: 'Honor',
};

type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

/** The editor page of one plan. */
export const commissionPlanEditPath = (resellerId: number, planId: number) =>
  `/partners/${resellerId}/commission-plans/${planId}/edit`;

/** Router state the editor reads: the list it came from, and the plan a Fix this plan copied. */
export interface FixedFromState {
  from?: string;
  fixedFrom?: string;
}

/**
 * The plan's terms in words: "10% for 12 months", "10% for life", or
 * "3 phases: 40% → 25% → 10%".
 */
export function termsSummary(phases: CommissionPhase[], t: Translate, locale: string): string {
  const [first] = phases;
  if (first === undefined) return '—';
  if (phases.length === 1) {
    const rate = formatPercentBp(first.rateBp, locale);
    if (first.toMonth === undefined) {
      return t('commissionPlans.terms.life', '{{rate}} for life', { rate });
    }
    const count = first.toMonth - first.fromMonth + 1;
    return count === 1
      ? t('commissionPlans.terms.oneMonth', '{{rate}} for 1 month', { rate })
      : t('commissionPlans.terms.months', '{{rate}} for {{count}} months', { rate, count });
  }
  return t('commissionPlans.terms.phases', '{{count}} phases: {{rates}}', {
    count: phases.length,
    rates: phases.map((phase) => formatPercentBp(phase.rateBp, locale)).join(' → '),
  });
}

/**
 * The 0105 terms, formatting only, one phrase per set component. Unset (`null`) is omitted —
 * the phase summary stands alone. Nothing here decides whether a plan may carry them.
 */
export function economicsSummary(
  plan: Pick<
    CommissionPlanView,
    | 'currency'
    | 'bountyMinor'
    | 'bountyOnPayment'
    | 'milestones'
    | 'perReferralCapMinor'
    | 'perAffiliateMonthCapMinor'
  >,
  t: Translate,
  locale: string,
): string[] {
  const lines: string[] = [];
  if (plan.bountyMinor != null && plan.bountyOnPayment != null) {
    lines.push(
      t('commissionPlans.terms.bounty', '{{amount}} on payment {{n}}', {
        amount: formatMoney(plan.bountyMinor, plan.currency, locale),
        n: plan.bountyOnPayment,
      }),
    );
  }
  const milestones = plan.milestones?.length ?? 0;
  if (milestones > 0) {
    lines.push(
      t('commissionPlans.terms.milestones', '{{count}} milestones', { count: milestones }),
    );
  }
  if (plan.perReferralCapMinor != null) {
    lines.push(
      t('commissionPlans.terms.perReferralCap', 'Per referral at most {{amount}}', {
        amount: formatMoney(plan.perReferralCapMinor, plan.currency, locale),
      }),
    );
  }
  if (plan.perAffiliateMonthCapMinor != null) {
    lines.push(
      t(
        'commissionPlans.terms.perAffiliateMonthCap',
        'Per affiliate per month at most {{amount}}',
        { amount: formatMoney(plan.perAffiliateMonthCapMinor, plan.currency, locale) },
      ),
    );
  }
  return lines;
}
